import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { trpc, trpcVanilla } from "@/lib/trpc";
import { authClient } from "@/lib/authClient";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import type { StaffRole } from "@/lib/rbac";

interface User {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  companyId?: string | null;
  isStaff?: boolean;
  staffRole?: StaffRole | null;
  /** Lot A : double authentification activée (auth.me). */
  totpEnabled?: boolean;
  /** Lot A : TOTP obligatoire (freelances de prospection). */
  mfaRequired?: boolean;
  /** Lot A (revue #336) : la session courante a prouvé le second facteur. */
  mfaSession?: boolean;
  /** Espace freelance pro : dossier d'accueil complet (null = non concerné). */
  freelanceOnboardingDone?: boolean | null;
}

/** Issue d'une tentative de connexion : session ouverte, ou code TOTP requis. */
export type LoginResult = "OK" | "TOTP_REQUIRED";

interface AuthContextType {
  user: User | null;
  /** Kept for back-compat with components that displayed the token. Always null now. */
  token: null;
  login: (email: string, password: string, captchaToken?: string, totpCode?: string) => Promise<LoginResult>;
  /** Relit le profil (après l'enrôlement TOTP). */
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Legacy keys from the localStorage-era — wiped on mount. */
const LEGACY_TOKEN_KEY = "admin_token";
const LEGACY_USER_KEY = "admin_user";

/**
 * Une session existe-t-elle ?
 *
 * On lisait `constrigo_csrf`, dont le maxAge est 1 h comme le jeton d'accès :
 * passé une heure, le navigateur le supprimait, `loginHint` retombait à `false`
 * et l'app n'ESSAYAIT MÊME PAS `auth.me` — écran déconnecté alors que le
 * refresh token (30 j) était intact. `constrigo_session` vit aussi longtemps
 * que le refresh, précisément pour répondre à cette question.
 */
function hasSessionCookie(): boolean {
  return authClient.hasSession();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Migrate away from localStorage: clear any leftover state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
  }, []);

  const [loginHint, setLoginHint] = useState<boolean>(hasSessionCookie);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: loginHint,
    retry: false,
    // On ne conclut à la déconnexion que sur un refus d'authentification franc.
    // Un 500 (hoquet backend) ou un 429 (limiteur générique, partagé par IP)
    // effaçait auparavant la session côté UI alors que les cookies restaient
    // parfaitement valides.
    onError: (err) => {
      const code = err.data?.code;
      if (code === "UNAUTHORIZED" || code === "FORBIDDEN") setLoginHint(false);
    },
  });

  // Renouvellement proactif : la session staff se prolonge toute seule tant que
  // le refresh token (30 j) vit, sans le 401 visible à chaque heure pleine.
  useEffect(() => {
    return authClient.startProactiveRefresh(() => setLoginHint(false));
  }, []);

  const loginMutation = trpc.auth.login.useMutation();

  const login = useCallback(async (email: string, password: string, captchaToken?: string, totpCode?: string): Promise<LoginResult> => {
    try {
      // captchaToken : `undefined` en usage normal. Le serveur ne le réclame
      // qu'au-delà d'un seuil d'échecs récents (CAPTCHA adaptatif), et le
      // signale alors via la sentinelle CAPTCHA_REQUIRED.
      const result = await loginMutation.mutateAsync({ email, password, captchaToken, totpCode });

      // Lot A : freelance enrôlé — le serveur n'émet AUCUN jeton sans code TOTP.
      if ("requiresTotp" in result && result.requiresTotp) {
        return "TOTP_REQUIRED";
      }

      // OPS is reserved for staff only. (`in` : seules les réponses staff
      // portent isStaff/staffRole dans l'union renvoyée par auth.login.)
      const loggedUser = result.user;
      if (!("isStaff" in loggedUser) || !loggedUser.isStaff || !("staffRole" in loggedUser) || !loggedUser.staffRole) {
        throw new Error("Accès refusé. Seuls les membres du staff peuvent accéder à cette interface.");
      }

      // Cookies have been set by the server. Mark the client as logged in
      // and trigger /auth.me to hydrate the full profile.
      setLoginHint(true);
      void meQuery.refetch();
      return "OK";
    } catch (error: any) {
      if (error?.message?.includes("Failed to fetch")) {
        throw new Error("Le serveur backend n'est pas accessible. Assurez-vous qu'il est démarré sur http://localhost:3001");
      }
      throw error;
    }
  }, [loginMutation, meQuery]);

  const logout = useCallback(async () => {
    // Purge l'horodatage « refresh récent » et prévient les autres onglets.
    authClient.markLoggedOut();
    try {
      await trpcVanilla.auth.logoutSelf.mutate(undefined as any);
    } catch {
      // Cookie might already be stale — ignore.
    }
    setLoginHint(false);
    if (typeof window !== "undefined") {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      localStorage.removeItem(LEGACY_USER_KEY);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await meQuery.refetch();
  }, [meQuery]);

  const user = (loginHint ? (meQuery.data as User | undefined) : null) ?? null;

  // Keep Sentry aware of WHO is connected so every event/log names the user.
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        role: user.staffRole ?? user.role,
        companyId: user.companyId ?? undefined,
      });
    } else {
      clearSentryUser();
    }
  }, [user]);

  const isLoading = loginHint && meQuery.isLoading;

  return (
    <AuthContext.Provider
      value={{
        user,
        token: null,
        login,
        logout,
        refreshUser,
        isAuthenticated: loginHint && !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
